import React, { useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import { ContentLayout, HeaderBreadcrumbs, Loader } from "../../../shared";
import { Button, Box, Typography, Chip } from "@mui/material";
import {
  PointPromotionDetailsTab,
  PointPromotionProducts,
  PointPromotionStoresTab,
} from "../components";
import { usePointPromotionDetails } from "../hooks";
import { CustomeDialog } from "../../../shared";

export const PointPromotionDetails = () => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const isStoreAdmin = pathname.includes("store-admin");
  const {
    state,
    handleStatusModal,
    handleConfirmStatusChange,
    handleDeleteModal,
    handleDeletePromotion,
  } = usePointPromotionDetails({ id, enableApi: true });
  const { data: promotionData } = state;

  const tabs = useMemo(
    () => [
      {
        label: "Details",
        value: "1",
        panel: <PointPromotionDetailsTab data={promotionData} id={id} />,
      },
      {
        label: "Products",
        value: "2",
        panel: <PointPromotionProducts data={promotionData} />,
      },
      {
        label: "Stores",
        value: "3",
        panel: <PointPromotionStoresTab data={promotionData} />,
      },
    ],
    [promotionData, id],
  );

  const breadcrumbLinks = [
    { name: "Dashboard", href: isStoreAdmin ? "/store-admin" : "/" },
    {
      name: "Point Promotions",
      href: isStoreAdmin
        ? "/store-admin/point-promotions"
        : "/point-promotions",
    },
    { name: promotionData?.name || "Details" },
  ];

  if (state.isLoading) {
    return <Loader />;
  }

  const isPublished = promotionData?.status === "published";
  const isDraft = !promotionData?.status || promotionData?.status === "draft";
  const isEnded = promotionData?.status === "ended";
  const isPublishAction = state.statusModalType === "publish";
  const status = promotionData?.status || "draft";

  const statusChip = {
    published: {
      label: "Published",
      color: "success",
      sx: { bgcolor: "#E8F5E914", border: "none !important" },
    },
    draft: {
      label: "Draft",
      color: "primary",
      sx: { bgcolor: "#1560D414", border: "none !important" },
    },
    unpublished: {
      label: "Paused",
      color: "warning",
      sx: { bgcolor: "#FFF4CE", border: "none !important" },
    },
    ended: {
      label: "Ended",
      color: "success",
      disabled: true,
    },
  }[status];

  return (
    <>
      <ContentLayout
        title={promotionData?.name || "Promotion Details"}
        breadcrumbs={<HeaderBreadcrumbs links={breadcrumbLinks} />}
        chipType="other"
        statusContent={
          statusChip ? (
            <Chip
              label={statusChip.label}
              color={statusChip.color}
              variant="outlined"
              size="small"
              sx={statusChip.sx}
              disabled={statusChip.disabled || false}
            />
          ) : null
        }
        rightContent={
          !isEnded ? (
            <>
              <Button
                type="button"
                size="medium"
                sx={{
                  minWidth: "40px",
                  p: "8px",
                }}
                onClick={() => handleDeleteModal(true)}
              >
                <img src="/icons/ic_delete_blue.svg" />
              </Button>
              {isPublished && (
                <Button
                  aria-label="pause"
                  color="primary"
                  type="button"
                  size="medium"
                  variant="text"
                  sx={{ minWidth: "40px", p: "8px" }}
                  onClick={() => handleStatusModal("unpublish", true)}
                >
                  <img src="/icons/play_icon.svg" alt="pause" />
                </Button>
              )}
              {promotionData?.status === "unpublished" && (
                <img
                  onClick={() => handleStatusModal("publish", true)}
                  src="/icons/ic_play_blue2.svg"
                  alt="publish"
                  style={{ cursor: "pointer" }}
                />
              )}
              {isDraft && (
                <Button
                  type="button"
                  size="medium"
                  variant="contained"
                  disabled={state.isSaveButtonBusy}
                  onClick={() => handleStatusModal("publish", true)}
                >
                  Publish
                </Button>
              )}
            </>
          ) : null
        }
        tabProps={{
          label: "pointPromotionDetails",
          list: tabs,
        }}
      ></ContentLayout>

      <CustomeDialog
        open={state.statusModalOpen}
        PaperProps={{
          sx: {
            width: "423px",
            height: "140px",
            p: "16px",
            gap: "12px",
          },
        }}
        title={isPublishAction ? "Publish Promotion" : "Pause Promotion"}
        content={
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Typography fontSize="14px" fontWeight={400} lineHeight="16px">
              {isPublishAction
                ? "Are you sure you want to publish this promotion?"
                : "Are you sure you want to pause this promotion?"}
            </Typography>
            <Box display="flex" justifyContent="flex-end" mt={2} gap={1.5}>
              <Button
                size="small"
                variant="text"
                color="info"
                onClick={() => handleStatusModal(state.statusModalType, false)}
              >
                No
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={state.isSaveButtonBusy}
                onClick={handleConfirmStatusChange}
              >
                Yes
              </Button>
            </Box>
          </Box>
        }
      />

      <CustomeDialog
        open={state.deleteModalOpen}
        PaperProps={{
          sx: {
            width: "423px",
            p: "16px",
            gap: "12px",
          },
        }}
        title="Delete Promotion"
        content={
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Typography fontSize="14px" fontWeight={400} lineHeight="16px">
              Are you sure you want to delete this promotion? This action cannot
              be undone.
            </Typography>
            <Box display="flex" justifyContent="flex-end" mt={2} gap={1.5}>
              <Button
                size="small"
                variant="text"
                color="info"
                onClick={() => handleDeleteModal(false)}
              >
                No
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                disabled={state.isSaveButtonBusy}
                onClick={handleDeletePromotion}
              >
                Delete
              </Button>
            </Box>
          </Box>
        }
      />
    </>
  );
};
